import {
  ConversationStarter,
  Question,
  Vocabulary,
  LanguageData,
} from '../types/data';

/**
 * Vocabulary
 * 10 most used words per difficulty level
 */
const vocabulary: Vocabulary[] = [
  // Beginner level
  {
    word: 'hallo',
    translation: 'hello',
    tags: ['DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'danke',
    translation: 'thank you',
    tags: ['DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'bitte',
    translation: "please/you're welcome",
    tags: ['DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'ja',
    translation: 'yes',
    tags: ['DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'nein',
    translation: 'no',
    tags: ['DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'Wasser',
    translation: 'water',
    tags: ['FoodAndDrink', 'DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'Essen',
    translation: 'food',
    tags: ['FoodAndDrink'],
    difficulty: 'Beginner',
  },
  {
    word: 'Toilette',
    translation: 'bathroom',
    tags: ['Travel', 'DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'guten Morgen',
    translation: 'good morning',
    tags: ['DailyLife'],
    difficulty: 'Beginner',
  },
  {
    word: 'auf Wiedersehen',
    translation: 'goodbye',
    tags: ['DailyLife'],
    difficulty: 'Beginner',
  },

  // Intermediate level
  {
    word: 'vielleicht',
    translation: 'maybe',
    tags: ['DailyLife'],
    difficulty: 'Intermediate',
  },
  {
    word: 'immer noch',
    translation: 'still/yet',
    tags: ['DailyLife'],
    difficulty: 'Intermediate',
  },
  {
    word: 'während',
    translation: 'while',
    tags: ['DailyLife'],
    difficulty: 'Intermediate',
  },
  {
    word: 'laut',
    translation: 'according to',
    tags: ['Business', 'Education'],
    difficulty: 'Intermediate',
  },
  {
    word: 'obwohl',
    translation: 'although',
    tags: ['DailyLife', 'Education'],
    difficulty: 'Intermediate',
  },
  {
    word: 'bekommen',
    translation: 'to get/obtain',
    tags: ['DailyLife', 'Business'],
    difficulty: 'Intermediate',
  },
  {
    word: 'entwickeln',
    translation: 'to develop',
    tags: ['Business', 'Technology'],
    difficulty: 'Intermediate',
  },
  {
    word: 'trotzdem',
    translation: 'however/nevertheless',
    tags: ['Education', 'Business'],
    difficulty: 'Intermediate',
  },
  {
    word: 'außerdem',
    translation: 'furthermore/besides',
    tags: ['Education', 'Business'],
    difficulty: 'Intermediate',
  },
  {
    word: 'einrichten',
    translation: 'to establish/set up',
    tags: ['Business', 'Education'],
    difficulty: 'Intermediate',
  },

  // Advanced level
  {
    word: 'unerlässlich',
    translation: 'essential',
    tags: ['Business', 'Education'],
    difficulty: 'Advanced',
  },
  {
    word: 'bedauerlicherweise',
    translation: 'unfortunately',
    tags: ['DailyLife', 'Business'],
    difficulty: 'Advanced',
  },
  {
    word: 'gleichzeitig',
    translation: 'simultaneously',
    tags: ['Education', 'Technology'],
    difficulty: 'Advanced',
  },
  {
    word: 'zweifellos',
    translation: 'undoubtedly',
    tags: ['Education', 'Business'],
    difficulty: 'Advanced',
  },
  {
    word: 'verzichtbar',
    translation: 'dispensable',
    tags: ['Business', 'Education'],
    difficulty: 'Advanced',
  },
  {
    word: 'beanspruchen',
    translation: 'to claim/demand',
    tags: ['Business', 'CurrentEvents'],
    difficulty: 'Advanced',
  },
  {
    word: 'geringschätzen',
    translation: 'to underestimate/belittle',
    tags: ['DailyLife', 'Education'],
    difficulty: 'Advanced',
  },
  {
    word: 'ausführen',
    translation: 'to detail/execute',
    tags: ['Business', 'Education'],
    difficulty: 'Advanced',
  },
  {
    word: 'gedankenverloren',
    translation: 'absorbed in thought',
    tags: ['Literature', 'Arts'],
    difficulty: 'Advanced',
  },
  {
    word: 'Eigenart',
    translation: 'idiosyncrasy',
    tags: ['Culture', 'Education'],
    difficulty: 'Advanced',
  },
];

/**
 * Suggested Questions
 * 10 most used questions per difficulty level
 */
const suggestedQuestions: Question[] = [
  // Beginner level
  {
    question: 'Wie heißt du?',
    difficulty: 'Beginner',
    tags: ['DailyLife'],
  },
  {
    question: 'Woher kommst du?',
    difficulty: 'Beginner',
    tags: ['DailyLife', 'Travel'],
  },
  {
    question: 'Wie spät ist es?',
    difficulty: 'Beginner',
    tags: ['DailyLife'],
  },
  {
    question: 'Wo ist die Toilette?',
    difficulty: 'Beginner',
    tags: ['Travel', 'DailyLife'],
  },
  {
    question: 'Wie viel kostet das?',
    difficulty: 'Beginner',
    tags: ['Travel', 'FoodAndDrink', 'DailyLife'],
  },
  {
    question: 'Sprichst du Englisch?',
    difficulty: 'Beginner',
    tags: ['Travel', 'DailyLife'],
  },
  {
    question: 'Wie geht es dir?',
    difficulty: 'Beginner',
    tags: ['DailyLife'],
  },
  {
    question: 'Was ist das?',
    difficulty: 'Beginner',
    tags: ['DailyLife'],
  },
  {
    question: 'Hast du Wasser?',
    difficulty: 'Beginner',
    tags: ['FoodAndDrink', 'DailyLife'],
  },
  {
    question: 'Wo ist das Hotel?',
    difficulty: 'Beginner',
    tags: ['Travel'],
  },

  // Intermediate level
  {
    question: 'Was machst du gerne in deiner Freizeit?',
    difficulty: 'Intermediate',
    tags: ['DailyLife', 'Hobbies'],
  },
  {
    question: 'Was ist dein Lieblingsfilm und warum?',
    difficulty: 'Intermediate',
    tags: ['Entertainment', 'DailyLife'],
  },
  {
    question: 'Wie kommst du normalerweise zur Arbeit?',
    difficulty: 'Intermediate',
    tags: ['DailyLife', 'Travel'],
  },
  {
    question: 'Welche Art von Musik hörst du gerne?',
    difficulty: 'Intermediate',
    tags: ['Music', 'Entertainment'],
  },
  {
    question: 'Kannst du mir ein gutes Restaurant in der Nähe empfehlen?',
    difficulty: 'Intermediate',
    tags: ['FoodAndDrink', 'Travel'],
  },
  {
    question: 'Was denkst du über die aktuelle politische Situation?',
    difficulty: 'Intermediate',
    tags: ['CurrentEvents', 'Culture'],
  },
  {
    question: 'Wie hat sich deine Stadt in den letzten Jahren verändert?',
    difficulty: 'Intermediate',
    tags: ['Culture', 'DailyLife'],
  },
  {
    question: 'Was würdest du tun, wenn du im Lotto gewinnen würdest?',
    difficulty: 'Intermediate',
    tags: ['DailyLife'],
  },
  {
    question: 'Bist du in letzter Zeit irgendwohin interessantes gereist?',
    difficulty: 'Intermediate',
    tags: ['Travel', 'DailyLife'],
  },
  {
    question: 'Was sind deine Ziele für das nächste Jahr?',
    difficulty: 'Intermediate',
    tags: ['DailyLife', 'Business'],
  },

  // Advanced level
  {
    question:
      'Wie glaubst du wird die künstliche Intelligenz unsere Gesellschaft in den kommenden Jahrzehnten verändern?',
    difficulty: 'Advanced',
    tags: ['Technology', 'CurrentEvents', 'Science'],
  },
  {
    question:
      'Welche Maßnahmen hältst du für am effektivsten, um den Klimawandel auf individueller und staatlicher Ebene zu bekämpfen?',
    difficulty: 'Advanced',
    tags: ['CurrentEvents', 'Science', 'Education'],
  },
  {
    question:
      'Inwiefern hat die Globalisierung die kulturellen Identitäten in deinem Land beeinflusst?',
    difficulty: 'Advanced',
    tags: ['Culture', 'CurrentEvents', 'Business'],
  },
  {
    question:
      'Wie stehst du zum Gleichgewicht zwischen persönlicher Privatsphäre und nationaler Sicherheit im digitalen Zeitalter?',
    difficulty: 'Advanced',
    tags: ['Technology', 'CurrentEvents'],
  },
  {
    question:
      'Wie hat sich deine Perspektive auf Bildung im Laufe deines Lebens entwickelt?',
    difficulty: 'Advanced',
    tags: ['Education', 'DailyLife'],
  },
  {
    question:
      'Welche Rolle sollte Kunst in einer zunehmend technologieorientierten Gesellschaft spielen?',
    difficulty: 'Advanced',
    tags: ['Arts', 'Technology', 'Culture'],
  },
  {
    question:
      'Inwieweit beeinflussen deiner Meinung nach die Medien die Bildung der öffentlichen Meinung?',
    difficulty: 'Advanced',
    tags: ['CurrentEvents', 'Technology'],
  },
  {
    question:
      'Wie würdest du mit einem ethischen Dilemma umgehen, bei dem deine persönlichen Werte mit deinen beruflichen Pflichten in Konflikt geraten?',
    difficulty: 'Advanced',
    tags: ['Business', 'Education'],
  },
  {
    question:
      'Inwiefern spiegelt Literatur deiner Meinung nach die Werte einer Gesellschaft wider und prägt sie gleichzeitig?',
    difficulty: 'Advanced',
    tags: ['Literature', 'Culture', 'Education'],
  },
  {
    question:
      'Was sind die bedeutendsten Herausforderungen, mit denen zeitgenössische Demokratien konfrontiert sind?',
    difficulty: 'Advanced',
    tags: ['CurrentEvents', 'Education'],
  },
];

/**
 * Conversation Starters
 * 10 most used conversation starters per difficulty level
 */
const conversationStarters: ConversationStarter[] = [
  // Beginner level
  {
    sentence: 'Ich mag deutsches Essen sehr.',
    translation: 'I really like German food.',
    difficulty: 'Beginner',
    tags: ['FoodAndDrink', 'Culture'],
  },
  {
    sentence: 'Heute ist das Wetter sehr schön.',
    translation: 'The weather is very nice today.',
    difficulty: 'Beginner',
    tags: ['DailyLife'],
  },
  {
    sentence: 'Meine Familie kommt aus Berlin.',
    translation: 'My family is from Berlin.',
    difficulty: 'Beginner',
    tags: ['DailyLife', 'Travel'],
  },
  {
    sentence: 'Ich liebe es, durch Deutschland zu reisen.',
    translation: 'I love traveling around Germany.',
    difficulty: 'Beginner',
    tags: ['Travel', 'DailyLife'],
  },
  {
    sentence: 'Ich lerne Deutsch.',
    translation: 'I am learning German.',
    difficulty: 'Beginner',
    tags: ['Education', 'DailyLife'],
  },
  {
    sentence: 'Ich möchte mein Deutsch üben.',
    translation: 'I would like to practice my German.',
    difficulty: 'Beginner',
    tags: ['Education', 'DailyLife'],
  },
  {
    sentence: 'Ich wohne in einer kleinen Stadt.',
    translation: 'I live in a small city.',
    difficulty: 'Beginner',
    tags: ['DailyLife'],
  },
  {
    sentence: 'Ich höre gerne deutsche Musik.',
    translation: 'I like listening to German music.',
    difficulty: 'Beginner',
    tags: ['Music', 'Culture'],
  },
  {
    sentence: 'Ich arbeite als Lehrer.',
    translation: 'I work as a teacher.',
    difficulty: 'Beginner',
    tags: ['DailyLife', 'Education'],
  },
  {
    sentence: 'Mein Lieblingssport ist Fußball.',
    translation: 'My favorite sport is soccer.',
    difficulty: 'Beginner',
    tags: ['Sports', 'DailyLife'],
  },

  // Intermediate level
  {
    sentence:
      'In letzter Zeit denke ich darüber nach, in ein deutschsprachiges Land zu ziehen, um mein Deutsch zu verbessern.',
    translation:
      'Lately I have been thinking about moving to a German-speaking country to improve my German.',
    difficulty: 'Intermediate',
    tags: ['Travel', 'Education', 'DailyLife'],
  },
  {
    sentence:
      'Die deutsche Küche ist sehr vielfältig, jede Region hat ihre typischen Gerichte.',
    translation:
      'German cuisine is very diverse, each region has its typical dishes.',
    difficulty: 'Intermediate',
    tags: ['FoodAndDrink', 'Culture', 'Travel'],
  },
  {
    sentence:
      'Ich würde gerne mehr über die Geschichte und Kultur der deutschsprachigen Länder erfahren.',
    translation:
      'I would love to learn more about the history and culture of German-speaking countries.',
    difficulty: 'Intermediate',
    tags: ['Culture', 'Education', 'Travel'],
  },
  {
    sentence:
      'Ich finde, dass das deutsche Kino sehr talentierte Regisseure wie Werner Herzog und Wim Wenders hat.',
    translation:
      'I think German cinema has very talented directors like Werner Herzog and Wim Wenders.',
    difficulty: 'Intermediate',
    tags: ['Entertainment', 'Culture', 'Arts'],
  },
  {
    sentence:
      'Ich habe bemerkt, dass es viele Unterschiede zwischen dem Deutsch aus Deutschland, Österreich und der Schweiz gibt.',
    translation:
      'I have noticed that there are many differences between the German from Germany, Austria, and Switzerland.',
    difficulty: 'Intermediate',
    tags: ['Culture', 'Education'],
  },
  {
    sentence:
      'Ich lese gerade einen Roman von Hermann Hesse, um meinen Wortschatz zu verbessern.',
    translation:
      'I am reading a novel by Hermann Hesse to improve my vocabulary.',
    difficulty: 'Intermediate',
    tags: ['Literature', 'Education'],
  },
  {
    sentence:
      'Ich würde gerne das Brandenburger Tor besuchen, wenn ich das nächste Mal nach Berlin fahre.',
    translation:
      'I would like to visit the Brandenburg Gate the next time I go to Berlin.',
    difficulty: 'Intermediate',
    tags: ['Travel', 'Arts', 'Culture'],
  },
  {
    sentence:
      'Meiner Meinung nach ist das Erlernen von Sprachen eine der besten Möglichkeiten, verschiedene Kulturen zu verstehen.',
    translation:
      'In my opinion, learning languages is one of the best ways to understand different cultures.',
    difficulty: 'Intermediate',
    tags: ['Education', 'Culture'],
  },
  {
    sentence: 'Ich wollte schon immer das Oktoberfest in München besuchen.',
    translation: 'I have always wanted to attend the Oktoberfest in Munich.',
    difficulty: 'Intermediate',
    tags: ['Travel', 'Culture', 'Entertainment'],
  },
  {
    sentence:
      'Deutsche Autos sind für ihre Qualität und Zuverlässigkeit weltweit bekannt.',
    translation:
      'German cars are known worldwide for their quality and reliability.',
    difficulty: 'Intermediate',
    tags: ['Technology', 'Culture', 'Business'],
  },

  // Advanced level
  {
    sentence:
      'Die sprachliche Vielfalt in der deutschsprachigen Welt, mit ihren zahlreichen Dialekten und regionalen Besonderheiten, spiegelt die kulturelle Reichhaltigkeit dieser Länder wider und wirft interessante Fragen zur sprachlichen Identität auf.',
    translation:
      'The linguistic diversity in the German-speaking world, with its numerous dialects and regional peculiarities, reflects the cultural richness of these countries and raises interesting questions about linguistic identity.',
    difficulty: 'Advanced',
    tags: ['Culture', 'Education', 'CurrentEvents'],
  },
  {
    sentence:
      'Die deutsche Romantik, mit Vertretern wie Goethe und Schiller, hat einen bedeutenden Einfluss auf die Weltliteratur und -philosophie ausgeübt und prägt bis heute unser Verständnis von Kunst und Individualität.',
    translation:
      'German Romanticism, with representatives such as Goethe and Schiller, has exerted a significant influence on world literature and philosophy and continues to shape our understanding of art and individuality to this day.',
    difficulty: 'Advanced',
    tags: ['Literature', 'Arts', 'Culture'],
  },
  {
    sentence:
      'Die Aufarbeitung der NS-Vergangenheit in Deutschland gilt international als vorbildlich, obwohl der Prozess komplex und nicht ohne Kontroversen war und bis heute andauert.',
    translation:
      'The process of coming to terms with the Nazi past in Germany is considered exemplary internationally, although the process was complex and not without controversy and continues to this day.',
    difficulty: 'Advanced',
    tags: ['CurrentEvents', 'Education', 'Culture'],
  },
  {
    sentence:
      'Der demografische Wandel und die alternde Bevölkerung stellen eine der größten gesellschaftlichen Herausforderungen für Deutschland in den kommenden Jahrzehnten dar.',
    translation:
      'Demographic change and the aging population represent one of the greatest social challenges for Germany in the coming decades.',
    difficulty: 'Advanced',
    tags: ['CurrentEvents', 'Culture', 'Business'],
  },
  {
    sentence:
      'Das duale Ausbildungssystem in Deutschland, das theoretisches Lernen mit praktischer Berufserfahrung verbindet, wird international als Modell für effektive berufliche Bildung angesehen.',
    translation:
      'The dual education system in Germany, which combines theoretical learning with practical work experience, is internationally regarded as a model for effective vocational education.',
    difficulty: 'Advanced',
    tags: ['Education', 'Business', 'Culture'],
  },
  {
    sentence:
      'Die Energiewende in Deutschland, der geplante Übergang zu erneuerbaren Energien und der Ausstieg aus der Kernenergie, stellt ein ambitioniertes Projekt dar, das weltweit beobachtet wird.',
    translation:
      'The energy transition in Germany, the planned transition to renewable energies and the phase-out of nuclear energy, represents an ambitious project that is being observed worldwide.',
    difficulty: 'Advanced',
    tags: ['Technology', 'Science', 'CurrentEvents'],
  },
  {
    sentence:
      'Die Philosophie des deutschen Idealismus, vertreten durch Denker wie Kant, Hegel und Schopenhauer, hat grundlegende Fragen zur Natur der Realität, des Wissens und der Ethik aufgeworfen, die bis heute relevant sind.',
    translation:
      'The philosophy of German idealism, represented by thinkers such as Kant, Hegel, and Schopenhauer, has raised fundamental questions about the nature of reality, knowledge, and ethics that remain relevant today.',
    difficulty: 'Advanced',
    tags: ['Education', 'Culture', 'Literature'],
  },
  {
    sentence:
      'Die Rolle Deutschlands in der Europäischen Union hat sich im Laufe der Jahre gewandelt, von einem zurückhaltenden Partner zu einer führenden wirtschaftlichen und politischen Kraft.',
    translation:
      "Germany's role in the European Union has changed over the years, from a restrained partner to a leading economic and political force.",
    difficulty: 'Advanced',
    tags: ['Business', 'CurrentEvents', 'Education'],
  },
  {
    sentence:
      'Die Bauhaus-Bewegung, die in Deutschland entstand, revolutionierte die Architektur und das Design des 20. Jahrhunderts durch ihre Verbindung von Funktionalität, Einfachheit und ästhetischer Qualität.',
    translation:
      'The Bauhaus movement, which originated in Germany, revolutionized 20th century architecture and design through its combination of functionality, simplicity, and aesthetic quality.',
    difficulty: 'Advanced',
    tags: ['Arts', 'Culture', 'Education'],
  },
  {
    sentence:
      'Die Konzepte der sozialen Marktwirtschaft und des Wohlfahrtsstaates, wie sie in Deutschland entwickelt wurden, bieten interessante Perspektiven auf das Verhältnis zwischen wirtschaftlicher Effizienz und sozialer Gerechtigkeit.',
    translation:
      'The concepts of the social market economy and the welfare state, as developed in Germany, offer interesting perspectives on the relationship between economic efficiency and social justice.',
    difficulty: 'Advanced',
    tags: ['Business', 'CurrentEvents', 'Education'],
  },
];

export const german: LanguageData = {
  vocabulary,
  suggestedQuestions,
  conversationStarters,
};
